import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { of } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { ApplicationContextService } from 'src/app/core/services/application-context.service';
import { ApplicationContext } from 'src/app/core/models/application-context.model';
import { AuthenticationService } from 'src/app/core/services/auth.service';
import { ClinicCompanyAutoSelectService } from 'src/app/core/services/clinic-company-auto-select.service';
import { resolveClinicAccess } from 'src/app/core/utils/organization-services-license.util';
import {
  clearLoginHandoffPending,
  isAskAyaClinicLaunch,
  markLoginHandoffPending,
  setLaunchRequestedCompanyId,
} from 'src/app/core/utils/clinic-launch-context.util';
import { shouldPromptCompanySelectionOnDirectLogin } from 'src/app/core/utils/account-organization.util';
import { LocalStorageService } from 'src/app/core/services/local-storage.service';

@Component({
  selector: 'app-redirect',
  templateUrl: './app-redirect.component.html',
  styleUrls: ['./app-redirect.component.scss']
})
export class AppRedirectComponent implements OnInit {
  constructor(
    private readonly _router: Router,
    private readonly _authService: AuthenticationService,
    private readonly _localStorageService: LocalStorageService,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _appContextService: ApplicationContextService,
    private readonly _clinicCompanyAutoSelect: ClinicCompanyAutoSelectService) {
  }

  ngOnInit() {
    this._localStorageService.authorizationData = null;
    this._appContextService.resetContext();

    const params = this._activatedRoute.snapshot.queryParams;
    const patientId = params['id'] ? String(params['id']).trim() : null;
    const requestedCompanyId = params['companyId']
      ? String(params['companyId']).trim()
      : null;
    const requestedUserId = params['userId']
      ? String(params['userId']).trim()
      : null;
    const isFromAyaLaunch = isAskAyaClinicLaunch({
      token: params['token'],
      q: params['q'],
      launchSource: params['launchSource'],
      companyId: requestedCompanyId,
      userId: requestedUserId,
    });

    const hydrated = this._authService.hydrateAuthFromQueryParams({
      token: params['token'],
      refreshToken: params['refreshToken'],
      q: params['q'],
      d: params['d'],
    });

    if (!hydrated) {
      this._authService.clearSessionAndRedirectToLogin();
      return;
    }

    markLoginHandoffPending();

    if (requestedCompanyId && isFromAyaLaunch) {
      setLaunchRequestedCompanyId(requestedCompanyId);
    }

    const loadContext$ = () =>
      this._appContextService.context.pipe(take(1));

    const ensureAyaLaunchCompany$ = (context: ApplicationContext) => {
      if (!isFromAyaLaunch || !requestedCompanyId || !requestedUserId) {
        return of(context);
      }

      const activeId =
        context.currentlyLoggedInUser?.companyId?.toLowerCase();
      if (activeId === requestedCompanyId.toLowerCase()) {
        return of(context);
      }

      if (this._authService.isIdentityAccessToken()) {
        throw new Error(
          'Failed to scope Clinic session to workspace organization'
        );
      }

      return this._clinicCompanyAutoSelect
        .switchToCompanyIds(requestedUserId, requestedCompanyId)
        .pipe(
          switchMap((scoped) => {
            if (!scoped) {
              throw new Error(
                'Failed to scope Clinic session to workspace organization'
              );
            }
            return loadContext$();
          }),
          switchMap((scopedContext) => {
            const scopedCompanyId =
              scopedContext.currentlyLoggedInUser?.companyId?.toLowerCase();
            if (scopedCompanyId !== requestedCompanyId.toLowerCase()) {
              throw new Error(
                'Failed to scope Clinic session to workspace organization'
              );
            }
            return of(scopedContext);
          })
        );
    };

    // Navigate once after context is ready. Without take(1), this subscription
    // leaks after leaving /app-redirect and forces navigateByUrl('/') on every
    // later context update (e.g. Scribe ReferralDataLoaderResolver).
    loadContext$()
      .pipe(switchMap((context) => ensureAyaLaunchCompany$(context)))
      .subscribe({
        next: (context) => {
          if (!context?.currentlyLoggedInUser?.id) {
            clearLoginHandoffPending();
            this._authService.clearSessionAndRedirectToLogin();
            return;
          }

          const destination = patientId ? `patients/${patientId}` : '/';

          if (
            shouldPromptCompanySelectionOnDirectLogin({
              userCompanies: context.userCompanies,
              isFromAyaLaunch,
            })
          ) {
            void this._router.navigate(['/select-company'], {
              queryParams: { reason: 'multi-org' },
              replaceUrl: true,
            });
            return;
          }

          clearLoginHandoffPending();

          const access = resolveClinicAccess(context);
          if (access === 'no-access') {
            void this._router.navigateByUrl('/no-clinic-access', {
              replaceUrl: true,
            });
            return;
          }
          if (access === 'select-company') {
            void this._router.navigate(['/select-company'], {
              queryParams: { reason: 'no-clinic-license' },
              replaceUrl: true,
            });
            return;
          }

          void this._router.navigateByUrl(destination);
        },
        error: () => {
          clearLoginHandoffPending();
          this._authService.clearSessionAndRedirectToLogin();
        },
      });
  }
}
